import { useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { useUpgradeCandidates } from '../../queries';

const NULL_SYMBOL = '—';

const initials = (name: string | null | undefined): string => {
  const safe = String(name || '').trim();
  if (!safe) return '?';
  const parts = safe.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || safe[0]!.toUpperCase();
};

const UpgradeOpportunitiesPanel = () => {
  const [thresholdPct, setThresholdPct] = useState(80);
  const query = useUpgradeCandidates(thresholdPct, true);
  const rows = query.data ?? [];

  return (
    <article className="actions-panel" data-testid="upgrade-opportunities-panel">
      <header className="actions-panel__header">
        <span className="actions-panel__icon" aria-hidden="true">
          <TrendingUp size={16} />
        </span>
        <div className="actions-panel__title-block">
          <h3 className="actions-panel__title">Upgrade opportunities</h3>
        </div>
        <label className="actions-panel__threshold" aria-label="Upgrade threshold">
          <span>Threshold</span>
          <input
            type="range"
            min={50}
            max={95}
            step={5}
            value={thresholdPct}
            onChange={(event) => setThresholdPct(Number(event.target.value))}
            data-testid="upgrade-threshold-slider"
          />
          <span className="actions-panel__threshold-value">{thresholdPct}%</span>
        </label>
      </header>

      {query.isLoading ? (
        <div className="actions-panel__loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="actions-panel__empty">
          No members above the {thresholdPct}% threshold right now.
        </div>
      ) : (
        <ul className="actions-panel__list">
          {rows.map((row) => (
            <li
              key={row.membershipUuid}
              className="actions-panel__item"
              data-testid={`upgrade-candidate-${row.membershipUuid}`}
            >
              <span className="actions-panel__avatar" aria-hidden="true">
                {initials(row.customerName)}
              </span>
              <div className="actions-panel__item-body">
                <div className="actions-panel__item-name">{row.customerName || NULL_SYMBOL}</div>
                <div className="actions-panel__item-sub">
                  {row.currentTierName || NULL_SYMBOL} → {row.nextTierName || NULL_SYMBOL}
                </div>
                <div className="actions-panel__progress" aria-label={`${row.progressPercent}% to next tier`}>
                  <div
                    className="actions-panel__progress-fill"
                    style={{ width: `${Math.min(100, Math.max(0, row.progressPercent))}%` }}
                  />
                </div>
              </div>
              <div className="actions-panel__item-num">
                <span className="actions-panel__item-num-value">{row.progressPercent}%</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
};

export default UpgradeOpportunitiesPanel;
